import { ACTIVITY_KEY, LAST_API_KEY, REDIRECT_KEY } from './sessionKeys'

/** Suppress other-tab session dialog during intentional logout / 401 redirect. */
let suppressOtherTabDialog = false

export function beginIntentionalSessionEnd(): void {
  suppressOtherTabDialog = true
}

export function isIntentionalSessionEnd(): boolean {
  return suppressOtherTabDialog
}

export function setActivityTime(timestamp: number = Date.now()): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(timestamp))
  } catch {
    /* ignore */
  }
}

export function getActivityTime(): number {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY)
    if (!raw) return 0
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return 0
  }
}

export function setLastApiRequestTime(timestamp: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_API_KEY, String(timestamp))
  } catch {
    /* ignore */
  }
}

export function getLastApiRequestTime(): number {
  try {
    const raw = localStorage.getItem(LAST_API_KEY)
    if (!raw) return 0
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return 0
  }
}

/** Where this tab was working, for restoration after re-authentication. */
export function currentLocationForRedirect(): string {
  const { pathname, search, hash } = window.location
  return pathname + search + hash
}

/**
 * Stored in sessionStorage so every tab keeps its own target: two tabs on
 * different pages must not overwrite each other, and logging in through one of
 * them must not drag the others to that page.
 */
export function setPostLoginRedirect(path: string): void {
  if (!path || path === '/login' || path.startsWith('/auth/')) return
  try {
    sessionStorage.setItem(REDIRECT_KEY, path)
  } catch {
    /* ignore */
  }
}

function readStoredRedirect(): string | null {
  try {
    // Prefer sessionStorage (per-tab). Fall back to localStorage once for older builds.
    return sessionStorage.getItem(REDIRECT_KEY) ?? localStorage.getItem(REDIRECT_KEY)
  } catch {
    return null
  }
}

function clearStoredRedirect(): void {
  try {
    sessionStorage.removeItem(REDIRECT_KEY)
    localStorage.removeItem(REDIRECT_KEY)
  } catch {
    /* ignore */
  }
}

/** Read without clearing — safe for React Strict Mode double-effects. */
export function peekPostLoginRedirect(): string | null {
  const redirectTo = readStoredRedirect()
  if (!redirectTo || !isSafeInternalRedirect(redirectTo)) return null
  return redirectTo
}

/** Consume post-login redirect once; only same-origin relative paths. */
export function consumePostLoginRedirect(): string | null {
  const redirectTo = peekPostLoginRedirect()
  clearStoredRedirect()
  return redirectTo
}

export function isSafeInternalRedirect(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false
  if (path.includes('://')) return false
  if (path.startsWith('/login') || path.startsWith('/auth/')) return false
  return true
}
